import React, { useMemo, useState } from "react"

import { StarIcons } from "../community/starrating"
import FeatureIcon from "../feature/featureicon"
import Image from "../common/image"
import { readableNumber } from "../../utils/text"
import {
  CommunityQualitySection,
  ExecutiveSummaryCard,
  FeatureMixSection,
  KeepExploringFooter,
  MethodologyFAQSection,
  ReportPanel,
  ReportSection,
  ReportSectionAnchor,
  ReportStickyLayout,
  TopicComparisonSection,
  UserInterestSection,
} from "./statsReport/TopicStatsSections"

const BAR_COLOR = "250, 204, 21"
const CHART_LIMIT = 8

const TopicStatsPageContent = ({ topic, stats, report }) => {
  const [expandedCharts, setExpandedCharts] = useState({})

  const {
    total,
    platformBreakdown,
    commonFeatures,
    nicheFeatures,
    memberSizeDistribution,
    unknownMemberCount,
    accessBreakdown,
    foundedTimeline,
    addedTimeline,
    trafficStats,
    platformSupplyDemand,
    formatMix,
    reviewBreakdown,
    userClickInsights,
  } = stats

  const { takeaways, quality } = report
  const topicName = topic.name

  const toggleChart = key => {
    setExpandedCharts(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const hasSizeSection = memberSizeDistribution.some(item => item.count > 0)
  const hasSupplyDemand = platformSupplyDemand.available
  const hasInterestSection =
    hasSupplyDemand ||
    userClickInsights?.available ||
    trafficStats.topicRedirects
  const foundedYearCount =
    foundedTimeline.yearCount ?? foundedTimeline.items.length
  const hasFoundedChart = foundedYearCount >= 3
  const hasSparseFounded = foundedYearCount > 0 && foundedYearCount < 3
  const hasAddedTrends = addedTimeline.items.length >= 3
  const hasQuality = quality?.available
  const hasComparison = report.comparison.available
  const hasFormatMix = formatMix.available
  const hasPlatforms = platformBreakdown.length > 0 || hasFormatMix
  const hasAudience = hasSizeSection || accessBreakdown.length > 0
  const hasFeatures = commonFeatures.length > 0 || nicheFeatures.length > 0
  const hasTrends = hasAddedTrends || hasFoundedChart || hasSparseFounded

  const audienceInsight = takeaways.size || takeaways.access || null
  const trendsInsight =
    (hasAddedTrends && takeaways.trends) ||
    (hasFoundedChart && takeaways.founded) ||
    null

  const navSections = useMemo(() => {
    const sections = []
    if (hasPlatforms)
      sections.push({ id: "section-platforms", label: "Platforms" })
    if (hasAudience)
      sections.push({ id: "section-audience", label: "Audience" })
    if (hasComparison)
      sections.push({ id: "section-compare", label: "Compare" })
    if (hasFeatures)
      sections.push({ id: "section-features", label: "Features" })
    if (hasInterestSection)
      sections.push({ id: "section-interest", label: "Interest" })
    if (hasTrends) sections.push({ id: "section-trends", label: "Trends" })
    if (hasQuality) sections.push({ id: "section-quality", label: "Quality" })
    sections.push({ id: "section-methodology", label: "Methodology" })
    return sections
  }, [
    hasPlatforms,
    hasAudience,
    hasComparison,
    hasFeatures,
    hasInterestSection,
    hasTrends,
    hasQuality,
  ])

  if (!total) {
    return (
      <ExecutiveSummaryCard topic={topic} summary={report.executiveSummary} />
    )
  }

  const sizeChartItems = memberSizeDistribution.map(item => ({
    slug: item.key,
    name: item.label,
    count: item.count,
    percent: item.percent,
    subtitle: item.description,
  }))

  const platformItems = limitItems(
    platformBreakdown,
    expandedCharts.platforms,
    CHART_LIMIT
  )
  const commonFeatureItems = limitItems(
    commonFeatures,
    expandedCharts.commonFeatures,
    CHART_LIMIT
  )
  const nicheFeatureItems = limitItems(
    nicheFeatures,
    expandedCharts.nicheFeatures,
    CHART_LIMIT
  )

  const formatChartItems = formatMix.items.map(item => ({
    slug: item.key,
    name: item.name,
    count: item.count,
    percent: item.percent,
  }))

  return (
    <div className="space-y-4 sm:space-y-5">
      <ExecutiveSummaryCard topic={topic} summary={report.executiveSummary} />

      <ReportStickyLayout sections={navSections}>
        {hasPlatforms ? (
          <ReportSectionAnchor id="section-platforms">
            <ReportPanel>
              <ReportSection
                title={`Where ${topicName} Communities Are Hosted`}
                insight={takeaways.platforms}
                compact={false}
              >
                {platformBreakdown.length ? (
                  <>
                    <ul className="sr-only">
                      {platformBreakdown.map(item => (
                        <li key={item.slug}>
                          {item.name}: {item.count} communities ({item.percent}
                          %)
                        </li>
                      ))}
                    </ul>
                    <BarChart
                      items={platformItems.items}
                      showPlatformIcons={true}
                    />
                    <ShowMoreButton
                      hiddenCount={platformItems.hiddenCount}
                      total={platformBreakdown.length}
                      label="platforms"
                      onClick={() => toggleChart("platforms")}
                    />
                  </>
                ) : null}
                {hasFormatMix ? (
                  <div
                    className={
                      platformBreakdown.length
                        ? "mt-5 border-t border-gray-100 pt-4"
                        : ""
                    }
                  >
                    <h3 className="text-sm font-semibold text-gray-900">
                      Format mix
                    </h3>
                    <div className="mt-3">
                      <BarChart items={formatChartItems} />
                    </div>
                    {takeaways.formatMix ? (
                      <p className="mt-2 text-sm text-gray-500">
                        {takeaways.formatMix}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </ReportSection>
            </ReportPanel>
          </ReportSectionAnchor>
        ) : null}

        {hasAudience ? (
          <ReportSectionAnchor id="section-audience">
            <ReportPanel>
              <ReportSection
                title="Audience Size & Access"
                insight={audienceInsight}
                compact={false}
              >
                <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                  {hasSizeSection ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        Community size
                      </h3>
                      <ul className="sr-only">
                        {memberSizeDistribution.map(item => (
                          <li key={item.key}>
                            {item.label}: {item.count} communities (
                            {item.percent}%)
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3">
                        <BarChart items={sizeChartItems} />
                      </div>
                      {unknownMemberCount ? (
                        <p className="mt-2 text-xs text-gray-500">
                          {unknownMemberCount} communit
                          {unknownMemberCount === 1 ? "y has" : "ies have"}{" "}
                          unknown member counts.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {accessBreakdown.length ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        Free vs paid
                      </h3>
                      <ul className="sr-only">
                        {accessBreakdown.map(item => (
                          <li key={item.key}>
                            {item.label}: {item.count} communities (
                            {item.percent}%)
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3">
                        <DonutChart items={accessBreakdown} />
                      </div>
                    </div>
                  ) : null}
                </div>
              </ReportSection>
            </ReportPanel>
          </ReportSectionAnchor>
        ) : null}

        {hasComparison ? (
          <ReportSectionAnchor id="section-compare">
            <ReportPanel>
              <TopicComparisonSection
                topic={topic}
                comparison={report.comparison}
              />
            </ReportPanel>
          </ReportSectionAnchor>
        ) : null}

        {hasFeatures ? (
          <ReportSectionAnchor id="section-features">
            <FeatureMixSection
              insight={takeaways.features}
              commonFeatures={commonFeatures}
              nicheFeatures={nicheFeatures}
              commonItems={commonFeatureItems}
              nicheItems={nicheFeatureItems}
              onShowMoreCommon={() => toggleChart("commonFeatures")}
              onShowMoreNiche={() => toggleChart("nicheFeatures")}
              renderFeatureTable={items => (
                <FeatureTable items={items} compact={true} />
              )}
            />
          </ReportSectionAnchor>
        ) : null}

        {hasInterestSection ? (
          <ReportSectionAnchor id="section-interest">
            <ReportPanel>
              <UserInterestSection
                userClickInsights={userClickInsights}
                trafficStats={trafficStats}
                platformSupplyDemand={platformSupplyDemand}
                supplyDemandInsight={takeaways.platformSupplyDemand}
                clickTakeaway={takeaways.userClicks}
                renderBarChart={items => (
                  <BarChart items={items} formatCount={readableNumber} />
                )}
              />
            </ReportPanel>
          </ReportSectionAnchor>
        ) : null}

        {hasTrends ? (
          <ReportSectionAnchor id="section-trends">
            <ReportPanel compact={true}>
              <ReportSection
                title="Trends"
                insight={trendsInsight}
                compact={false}
              >
                {hasAddedTrends ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Added to Hive Index
                    </h3>
                    <div className="mt-3">
                      <TimelineChart
                        items={addedTimeline.items}
                        ariaLabel={`${topicName} communities added over time`}
                      />
                    </div>
                  </div>
                ) : null}

                {hasFoundedChart ? (
                  <div
                    className={
                      hasAddedTrends ? "mt-5 border-t border-gray-100 pt-4" : ""
                    }
                  >
                    <h3 className="text-sm font-semibold text-gray-900">
                      Founded year
                    </h3>
                    <div className="mt-3">
                      <TimelineChart
                        items={foundedTimeline.items}
                        ariaLabel={`${topicName} communities founded by year`}
                      />
                    </div>
                    {foundedTimeline.unknownCount ? (
                      <p className="mt-2 text-xs text-gray-500">
                        {foundedTimeline.unknownCount} communit
                        {foundedTimeline.unknownCount === 1
                          ? "y has"
                          : "ies have"}{" "}
                        no founding year on file.
                      </p>
                    ) : null}
                  </div>
                ) : hasSparseFounded ? (
                  <div
                    className={
                      hasAddedTrends ? "mt-5 border-t border-gray-100 pt-4" : ""
                    }
                  >
                    <h3 className="text-sm font-semibold text-gray-900">
                      Founded year
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Not enough founding-year data to chart yet.
                    </p>
                  </div>
                ) : null}
              </ReportSection>
            </ReportPanel>
          </ReportSectionAnchor>
        ) : null}

        {hasQuality ? (
          <ReportSectionAnchor id="section-quality">
            <CommunityQualitySection
              topic={topic}
              reviewBreakdown={reviewBreakdown}
              quality={quality}
              reviewChart={
                <BarChart items={reviewBreakdown.items} showStarLabels={true} />
              }
            />
          </ReportSectionAnchor>
        ) : null}

        <ReportSectionAnchor id="section-methodology">
          <MethodologyFAQSection
            methodology={report.methodology}
            faq={report.faq}
          />
        </ReportSectionAnchor>
      </ReportStickyLayout>

      <KeepExploringFooter footer={report.exploreFooter} />
    </div>
  )
}

const ShowMoreButton = ({ hiddenCount, total, label, onClick }) => {
  if (!hiddenCount) return null

  return (
    <button
      type="button"
      className="mt-2 text-xs font-semibold text-rose-500 hover:text-rose-600"
      onClick={onClick}
    >
      Show all ({total} {label})
    </button>
  )
}

const BarChart = ({
  items,
  showPlatformIcons = false,
  showStarLabels = false,
  formatCount,
}) => {
  const max = Math.max(...items.map(item => item.count), 1)

  return (
    <ul className="flex flex-col gap-px">
      {items.map(item => (
        <li key={item.slug || item.name}>
          <div className="group relative flex w-full min-w-0 items-stretch overflow-hidden rounded-md bg-gray-50 px-2 py-1.5 text-left text-sm">
            <div
              className="absolute inset-y-0 left-0 rounded-md"
              style={{
                width: `${barPercent(item.count, max)}%`,
                backgroundColor: `rgba(${BAR_COLOR}, ${barOpacity(
                  item.count,
                  max
                )})`,
              }}
              aria-hidden="true"
            />
            <div className="relative z-10 flex min-w-0 flex-1 items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {showPlatformIcons ? (
                  <PlatformIcon
                    slug={item.slug}
                    name={item.name}
                    logoSmall={item.logoSmall}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  {showStarLabels && item.star ? (
                    <div
                      className="inline-flex items-center gap-px"
                      aria-label={`${item.star} stars`}
                    >
                      <StarIcons
                        reviewAvg={String(item.star)}
                        className="h-3.5 w-3.5"
                      />
                    </div>
                  ) : (
                    <div className="truncate font-medium text-gray-800">
                      {item.name}
                    </div>
                  )}
                  {item.subtitle ? (
                    <div className="truncate text-xs text-gray-500">
                      {item.subtitle}
                    </div>
                  ) : null}
                </div>
              </div>
              <span className="flex-shrink-0 tabular-nums text-gray-700">
                <span className="font-semibold">
                  {formatCount ? formatCount(item.count) : item.count}
                </span>
                <span className="ml-1.5 text-xs text-gray-500">
                  ({item.percent}%)
                </span>
              </span>
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

const PlatformIcon = ({ slug, name, logoSmall }) => (
  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white">
    {slug !== "independent" && logoSmall ? (
      <img
        src={logoSmall}
        alt=""
        className="h-full w-full object-contain p-0.5"
        loading="lazy"
      />
    ) : (
      <Image
        className="h-full w-full object-contain p-1"
        fileName="icon.png"
        alt={name || "Independent"}
        objectFit="contain"
      />
    )}
  </div>
)

const FeatureTable = ({ items, compact = false }) => {
  if (!items.length) {
    return <p className="text-sm text-gray-500">No feature data available.</p>
  }

  return (
    <>
      <ul className="sr-only">
        {items.map(item => (
          <li key={item.slug}>
            {item.name}: {item.count} communities ({item.percent}%)
          </li>
        ))}
      </ul>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li
            key={item.slug}
            className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-sm"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-yellow-100 text-yellow-600">
                <FeatureIcon
                  feature={item.slug}
                  className="h-3.5 w-3.5 [&>svg]:h-full [&>svg]:w-full"
                />
              </span>
              <div className="min-w-0">
                <div className="truncate font-medium text-gray-900">
                  {item.name}
                </div>
                {!compact && item.description ? (
                  <div className="truncate text-xs text-gray-500">
                    {item.description}
                  </div>
                ) : null}
              </div>
            </div>
            <span className="flex-shrink-0 font-semibold tabular-nums text-gray-700">
              {item.percent}%
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

const DONUT_COLORS = ["#fbbf24", "#fde68a"]

const DonutChart = ({ items }) => {
  const total = items.reduce((sum, item) => sum + item.count, 0) || 1
  let cumulative = 0
  const segments = items.map((item, index) => {
    const start = (cumulative / total) * 360
    cumulative += item.count
    const end = (cumulative / total) * 360
    return {
      ...item,
      start,
      end,
      color: DONUT_COLORS[index % DONUT_COLORS.length],
    }
  })

  return (
    <div className="flex items-center gap-4" aria-hidden="true">
      <svg viewBox="0 0 42 42" className="h-20 w-20 flex-shrink-0 -rotate-90">
        <circle
          cx="21"
          cy="21"
          r="15.915"
          fill="transparent"
          stroke="#f3f4f6"
          strokeWidth="4"
        />
        {segments.map(segment => (
          <circle
            key={segment.key}
            cx="21"
            cy="21"
            r="15.915"
            fill="transparent"
            stroke={segment.color}
            strokeWidth="4"
            strokeDasharray={`${((segment.end - segment.start) / 360) *
              100} ${100 - ((segment.end - segment.start) / 360) * 100}`}
            strokeDashoffset={100 - (segment.start / 360) * 100}
          />
        ))}
      </svg>
      <ul className="min-w-0 flex-1 space-y-1 text-sm">
        {segments.map(segment => (
          <li
            key={segment.key}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex min-w-0 items-center gap-2 font-medium text-gray-800">
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
                aria-hidden="true"
              />
              {segment.label}
            </span>
            <span className="tabular-nums text-gray-600">
              {segment.count} ({segment.percent}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const TimelineChart = ({ items, ariaLabel }) => {
  const max = Math.max(...items.map(item => item.count), 1)
  const maxHeight = 96

  return (
    <>
      <ul className="sr-only">
        {items.map(item => (
          <li key={item.key}>
            {item.label}: {item.count} communities ({item.percent}%)
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto pb-1" role="img" aria-label={ariaLabel}>
        <div className="flex min-w-min items-end gap-1 px-0.5">
          {items.map(item => (
            <div
              key={item.key}
              className="flex w-9 flex-shrink-0 flex-col items-center sm:w-10"
            >
              {item.count ? (
                <span className="mb-1 text-[10px] font-medium tabular-nums text-gray-600">
                  {item.count}
                </span>
              ) : (
                <span className="mb-1 h-[14px]" aria-hidden="true" />
              )}
              <div
                className="w-full rounded-t-md"
                style={{
                  height: `${Math.max(
                    4,
                    Math.round((item.count / max) * maxHeight)
                  )}px`,
                  backgroundColor: `rgba(${BAR_COLOR}, ${barOpacity(
                    item.count,
                    max
                  )})`,
                }}
                title={`${item.label}: ${item.count}`}
              />
              <span className="mt-1 max-w-full truncate text-center text-[10px] leading-tight text-gray-500">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function limitItems(items, showAll, limit = CHART_LIMIT) {
  if (showAll || items.length <= limit) {
    return { items, hiddenCount: 0 }
  }
  return { items: items.slice(0, limit), hiddenCount: items.length - limit }
}

function barPercent(count, max, min = 8) {
  if (!count) return 4
  return Math.max(min, Math.round((count / max) * 100))
}

function barOpacity(count, max) {
  if (!count || !max) return 0
  return (count / max) * 0.65
}

export default TopicStatsPageContent
